import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts'
import { TabProps, RechartsClickData } from './types'
import { useFormatters } from '../../contexts/PreferencesContext'
import { ChartWrapper } from './ChartWrapper'
import { useDrilldownAction, OTIFDrilldownModal, OTDDrilldownModal } from '../drilldown'
import { useQualityMeasureLabel, useQualityMeasureDivisor } from '../../lib/qualityLabels'

export function GroupDetailKPITab({ data, currency: _currency, filters }: TabProps) {
  const { formatCurrency: _formatCurrency, formatNumber } = useFormatters()
  const { openDrilldown } = useDrilldownAction()
  const qualityLabel = useQualityMeasureLabel()
  const qualityDivisor = useQualityMeasureDivisor()

  // Delivery metric mode: conditionally show OTIF and/or OTD sections
  const deliveryMode = data.delivery_metric_mode ?? 'both'
  const showOTIF = deliveryMode === 'both' || deliveryMode === 'otif_only'
  const showOTD = deliveryMode === 'both' || deliveryMode === 'otd_only'

  // OTIF/OTD Modal state
  const [isOTIFModalOpen, setIsOTIFModalOpen] = useState(false)
  const [otifModalContext, setOtifModalContext] = useState<string | undefined>(undefined)
  const [isOTDModalOpen, setIsOTDModalOpen] = useState(false)
  const [otdModalContext, setOtdModalContext] = useState<string | undefined>(undefined)

  // Convert filters to drilldown format
  const drilldownFilters = filters ? {
    date_from: filters.dateFrom || undefined,
    date_to: filters.dateTo || undefined,
    customer_id: filters.customer ? parseInt(filters.customer) : undefined,
    customer_group: filters.customerGroup || undefined,  // CR-4886
    supplier_id: filters.supplier ? parseInt(filters.supplier) : undefined,
    site_id: filters.site ? parseInt(filters.site) : undefined,
    site_country: filters.siteCountry || undefined,
    customer_country: filters.customerCountry || undefined,
  } : undefined

  // Handle chart click for drill-down
  const handleChartClick = (chartType: string, clickData?: RechartsClickData) => {
    const customer = clickData?.activePayload?.[0]?.payload?.['customer'] as string | undefined

    switch (chartType) {
      case 'otif':
        setOtifModalContext(customer || 'All Customers')
        setIsOTIFModalOpen(true)
        break
      case 'otd':
        setOtdModalContext(customer || 'All Customers')
        setIsOTDModalOpen(true)
        break
      case 'ppm':
        openDrilldown('ppm', drilldownFilters, customer || 'All Customers')
        break
      default:
        openDrilldown('spend', drilldownFilters)
    }
  }

  // CR-4211: Use real data from API instead of Math.random()
  const otifTarget = data.targets?.otif ?? data.quality?.otifTarget ?? 85
  const ppmTarget = data.targets?.ppm ?? data.quality?.ppmTarget ?? 1500
  const otdTarget = data.targets?.otd ?? data.delivery?.otdTarget ?? 90

  const customerOtifData = data.siteKPI?.otifBySite?.map((site, idx) => {
    // Calculate lines delivered from matching delivery data
    const deliveryEntry = data.siteKPI?.deliveryBySite?.[idx]
    const linesDelivered = deliveryEntry
      ? deliveryEntry.onTime + deliveryEntry.early + deliveryEntry.late
      : site.orders || 0
    const linesFailed = linesDelivered > 0
      ? Math.round(linesDelivered * (100 - site.otif) / 100)
      : 0
    return {
      customer: site.site,
      otif: site.otif,
      target: site.target || otifTarget,
      linesDelivered,
      linesFailed,
    }
  }) || []

  const customerOtdData = data.siteKPI?.deliveryBySite?.map((site) => ({
    customer: site.site,
    onTime: site.onTime,
    early: site.early,
    late: site.late,
    total: site.onTime + site.early + site.late,
  })) || []

  const customerPpmData = data.siteKPI?.ppmBySite?.map((site) => ({
    customer: site.site,
    ppm: site.ppm,
    target: site.target || ppmTarget,
    // Reverse the divisor the backend used to compute `ppm`. Using a
    // hardcoded 1,000,000 here when the platform is configured for PPK
    // would inflate the rejected count by 1,000×. See
    // backend/core/quality.py::get_quality_divisor.
    rejected: site.parts > 0 ? Math.round(site.ppm * site.parts / qualityDivisor) : 0,
    delivered: site.parts,
  })) || []

  return (
    <div className="space-y-4">
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          Group Detail KPI provides supplier-level breakdown of performance metrics.
          Click on any supplier to drill down into their specific performance data.
        </p>
      </div>

      {/* Customer OTIF Breakdown - CR-3835: Fixed site name truncation */}
      {showOTIF && (
      <ChartWrapper
        title="OTIF by Supplier"
        data={customerOtifData}
        exportFilename="otif-by-customer"
        legend={[
          { color: '#10b981', label: 'OTIF %' },
          { color: '#ef4444', label: 'Target' },
        ]}
      >
        <ResponsiveContainer width="100%" height={350}>
          <BarChart
            data={customerOtifData}
            onClick={(e) => handleChartClick('otif', e)}
            style={{ cursor: 'pointer' }}
            margin={{ bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="customer"
              tick={{ fontSize: 9 }}
              angle={-45}
              textAnchor="end"
              height={120}
              interval={0}
            />
            <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
            <Tooltip
              cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
              formatter={(value: number, name: string) => [
                name === 'otif' ? `${value.toFixed(1)}%` : value,
                name === 'otif' ? 'OTIF' : name
              ]}
            />
            <Bar dataKey="otif" fill="#10b981" name="OTIF" />
            <ReferenceLine y={otifTarget} stroke="#ef4444" strokeDasharray="5 5" label={{ value: `Target ${otifTarget}%`, position: 'right' }} />
          </BarChart>
        </ResponsiveContainer>
      </ChartWrapper>
      )}

      {/* Customer OTD Breakdown - CR-3835: Fixed site name truncation */}
      {showOTD && (
      <ChartWrapper
        title="Delivery Performance by Supplier"
        data={customerOtdData}
        exportFilename="delivery-by-customer"
        legend={[
          { color: '#10b981', label: 'On Time' },
          { color: '#fbbf24', label: 'Early' },
          { color: '#ef4444', label: 'Late' },
        ]}
      >
        <ResponsiveContainer width="100%" height={350}>
          <BarChart
            data={customerOtdData}
            onClick={(e) => handleChartClick('otd', e)}
            style={{ cursor: 'pointer' }}
            margin={{ bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="customer"
              tick={{ fontSize: 9 }}
              angle={-45}
              textAnchor="end"
              height={120}
              interval={0}
            />
            <YAxis />
            <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
            <Bar dataKey="onTime" stackId="delivery" fill="#10b981" name="On Time" />
            <Bar dataKey="early" stackId="delivery" fill="#fbbf24" name="Early" />
            <Bar dataKey="late" stackId="delivery" fill="#ef4444" name="Late" />
          </BarChart>
        </ResponsiveContainer>
      </ChartWrapper>
      )}

      {/* Customer PPM Breakdown - CR-3835: Fixed site name truncation */}
      <ChartWrapper
        title={`${qualityLabel} by Supplier`}
        data={customerPpmData}
        exportFilename="ppm-by-customer"
        legend={[
          { color: '#ef4444', label: qualityLabel },
          { color: '#10b981', label: 'Target' },
        ]}
      >
        <ResponsiveContainer width="100%" height={350}>
          <BarChart
            data={customerPpmData}
            onClick={(e) => handleChartClick('ppm', e)}
            style={{ cursor: 'pointer' }}
            margin={{ bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="customer"
              tick={{ fontSize: 9 }}
              angle={-45}
              textAnchor="end"
              height={120}
              interval={0}
            />
            <YAxis />
            <Tooltip
              cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
              formatter={(value: number, name: string) => [
                formatNumber(value),
                name === 'ppm' ? qualityLabel : name
              ]}
            />
            <Bar dataKey="ppm" fill="#ef4444" name={qualityLabel} />
            <ReferenceLine y={ppmTarget} stroke="#10b981" strokeDasharray="5 5" label={{ value: `Target ${ppmTarget}`, position: 'right' }} />
          </BarChart>
        </ResponsiveContainer>
      </ChartWrapper>

      {/* Customer Summary Table */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Customer Performance Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 font-medium">Supplier</th>
                  {showOTIF && <th className="text-right py-2 px-3 font-medium">OTIF %</th>}
                  {showOTD && <th className="text-right py-2 px-3 font-medium">OTD %</th>}
                  <th className="text-right py-2 px-3 font-medium">{qualityLabel}</th>
                  <th className="text-right py-2 px-3 font-medium">Lines Delivered</th>
                  <th className="text-right py-2 px-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {customerOtifData.slice(0, 10).map((customer, idx) => {
                  const otdEntry = customerOtdData[idx]
                  const ppmEntry = customerPpmData[idx]
                  const otd = otdEntry ? (otdEntry.onTime / otdEntry.total * 100) : 0
                  const otifOk = !showOTIF || customer.otif >= otifTarget
                  const otdOk = !showOTD || otd >= otdTarget
                  const isGood = otifOk && otdOk && (ppmEntry?.ppm || 0) <= ppmTarget

                  return (
                    <tr
                      key={idx}
                      className="border-b hover:bg-muted/50 cursor-pointer"
                      onClick={() => {
                        setOtifModalContext(customer.customer)
                        setIsOTIFModalOpen(true)
                      }}
                    >
                      <td className="py-2 px-3">{customer.customer}</td>
                      {showOTIF && (
                      <td className={`text-right py-2 px-3 ${customer.otif >= otifTarget ? 'text-green-600' : 'text-red-600'}`}>
                        {customer.otif.toFixed(1)}%
                      </td>
                      )}
                      {showOTD && (
                      <td className={`text-right py-2 px-3 ${otd >= otdTarget ? 'text-green-600' : 'text-red-600'}`}>
                        {otd.toFixed(1)}%
                      </td>
                      )}
                      <td className={`text-right py-2 px-3 ${(ppmEntry?.ppm || 0) <= ppmTarget ? 'text-green-600' : 'text-red-600'}`}>
                        {formatNumber(ppmEntry?.ppm || 0)}
                      </td>
                      <td className="text-right py-2 px-3">{formatNumber(customer.linesDelivered)}</td>
                      <td className="text-right py-2 px-3">
                        <span className={`px-2 py-1 rounded text-xs ${isGood ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {isGood ? 'Good' : 'Attention'}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* OTIF Specialized Drill-Down Modal */}
      <OTIFDrilldownModal
        isOpen={isOTIFModalOpen}
        onClose={() => setIsOTIFModalOpen(false)}
        filters={drilldownFilters}
        contextLabel={otifModalContext}
      />

      {/* OTD Specialized Drill-Down Modal */}
      <OTDDrilldownModal
        isOpen={isOTDModalOpen}
        onClose={() => setIsOTDModalOpen(false)}
        filters={drilldownFilters}
        contextLabel={otdModalContext}
      />
    </div>
  )
}
